import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CONTENT_KINDS, type TemplateKind, useSaveAsTemplate } from '@/entities/template';
import { isApiError } from '@/shared/api';
import { FormError } from '@/shared/forms';
import { Button, Dialog, TextField, useToast } from '@/shared/ui';

import styles from './Templates.module.css';

/** "Save as template": a name for a personal template built from this task or column. */
export function SaveTemplateDialog({
  kind,
  sourceId,
  defaultName,
  onClose,
}: {
  kind: TemplateKind;
  sourceId: string;
  defaultName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveAsTemplate(kind, sourceId);
  const [name, setName] = useState(defaultName.slice(0, 100));
  // boards and databases: structure only unless the person explicitly asks for the content
  const [includeContent, setIncludeContent] = useState(false);
  const hasContent = CONTENT_KINDS.includes(kind);
  const code = isApiError(save.error) ? save.error.fieldErrors['name']?.[0] : undefined;
  const nameError = code
    ? t(`validation.${code}`, { defaultValue: t('validation.INVALID') })
    : undefined;
  const blank = name.trim() === '';

  return (
    <Dialog title={t(`templates.save.title.${kind}`)} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (blank || save.isPending) return;
          save.mutate(
            { name: name.trim(), includeContent: hasContent && includeContent },
            {
              onSuccess: () => {
                toast.show({ message: t('templates.save.done', { name: name.trim() }) });
                onClose();
              },
            },
          );
        }}
      >
        <p className={styles.note}>{t(`templates.save.note.${kind}`)}</p>
        {kind === 'board' && includeContent && (
          <p className={styles.note} data-testid="save-omitted">
            {t('templates.save.omitted.board')}
          </p>
        )}
        <TextField
          label={t('templates.name')}
          value={name}
          maxLength={100}
          autoFocus
          error={nameError}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        {hasContent && (
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={includeContent}
              onChange={(event) => {
                setIncludeContent(event.target.checked);
              }}
            />
            <span>
              <span className={styles.itemName}>
                {t(
                  kind === 'board'
                    ? 'templates.save.includeTasks'
                    : 'templates.save.includeRecords',
                )}
              </span>
              <span className={styles.itemMeta}>
                {t(
                  kind === 'board'
                    ? 'templates.save.includeTasksHint'
                    : 'templates.save.includeRecordsHint',
                )}
              </span>
            </span>
          </label>
        )}
        {nameError === undefined && <FormError error={save.error} />}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={blank || save.isPending}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
